import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Loader2, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TICKET_PRIORITY } from "@/lib/tickets";
import api, { apiError } from "@/lib/api";

const EMPTY = { location_name: "", street: "", zip: "", city: "", contact: "", phone: "", email: "", desired_code: "", hardware: "", desired_date: "", note: "", priority: "normal" };
const inp = "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-600 focus:outline-none focus:border-[#c00000]/40 transition";

export default function NewLocationDialog({ open, onClose, onSaved, de = true }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const required = ["location_name", "street", "zip", "city", "contact", "phone", "email"];

  const submit = async () => {
    for (const k of required) if (!String(f[k] || "").trim()) { toast.error(de ? "Bitte alle Pflichtfelder ausfüllen." : "Please fill all required fields."); return; }
    setBusy(true);
    try {
      await api.post("/tickets/location-request", f);
      toast.success(de ? "Antrag erstellt – wartet auf Freigabe." : "Request created – pending approval.");
      window.dispatchEvent(new Event("tickets:changed"));
      setF(EMPTY); onSaved && onSaved(); onClose && onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const Field = ({ label, k, type = "text", req, ph, full }) => (
    <label className={full ? "sm:col-span-2" : ""}>
      <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{label}{req && <span className="text-red-600"> *</span>}</span>
      <input data-testid={`newloc-${k}`} type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph || ""} className={inp} />
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent data-testid="newloc-dialog" className="max-w-2xl bg-gray-50 border-gray-200 text-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900"><Building2 className="w-5 h-5 text-[#c00000]" /> {de ? "Neuen Standort beantragen" : "Request new location"}</DialogTitle>
          <DialogDescription className="text-gray-600">
            {de ? "Der Standort wird erst nach interner Freigabe real angelegt und danach belieferbar." : "The location is only created after internal approval."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <Field label={de ? "Standortname" : "Location name"} k="location_name" req full ph="z. B. Filiale Frankfurt Nord" />
          <Field label={de ? "Straße & Nr." : "Street"} k="street" req full />
          <Field label="PLZ" k="zip" req />
          <Field label={de ? "Ort" : "City"} k="city" req />
          <Field label={de ? "Ansprechpartner" : "Contact"} k="contact" req />
          <Field label={de ? "Telefon" : "Phone"} k="phone" type="tel" req />
          <Field label="E-Mail" k="email" type="email" req full />
          <Field label={de ? "Gewünschter Standortcode" : "Desired code"} k="desired_code" ph={de ? "optional – sonst automatisch" : "optional"} />
          <label>
            <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{de ? "Priorität" : "Priority"}</span>
            <select data-testid="newloc-priority" value={f.priority} onChange={(e) => set("priority", e.target.value)} className={inp}>
              {TICKET_PRIORITY.map((p) => <option key={p.id} value={p.id} className="bg-gray-50">{de ? p.de : p.en}</option>)}
            </select>
          </label>
          <Field label={de ? "Benötigte Hardware / Menge" : "Needed hardware"} k="hardware" full ph={de ? "z. B. 2 Tablets, 1 Scanner" : ""} />
          <Field label={de ? "Wunschtermin" : "Desired date"} k="desired_date" type="date" />
          <label className="sm:col-span-2">
            <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{de ? "Bemerkung" : "Note"}</span>
            <textarea data-testid="newloc-note" value={f.note} onChange={(e) => set("note", e.target.value)} rows={2} className={inp} />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
          <button onClick={onClose} className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 transition-colors">{de ? "Abbrechen" : "Cancel"}</button>
          <button data-testid="newloc-submit" onClick={submit} disabled={busy} className="flex items-center gap-2 bg-[#b00000] hover:bg-[#c00000] text-gray-900 font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} {de ? "Antrag senden" : "Submit request"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
